import React, { FC, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Card,
  Drawer,
  Form,
  Radio,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { ItemType } from '../../inventory/type/item'
import { ItemCategoryLevel } from '../../inventory/type'
import { OverrideVendorSelect } from './OverrideVendorSelect'
import {
  CreateFilterOverride,
  GetFilterBaseConfig,
  UpdateFilterOverride,
} from '../graphql/filter-config.graphql'
import {
  CreateFilterOverrideInput,
  FilterOverrideAction,
  FilterOverrideRuleInput,
  FilterOverrideScope,
  FilterOverrideView,
  UpdateFilterOverrideInput,
} from '../type/filter-config'
import {
  FILTER_CATALOG,
  FilterKind,
  getFilterDescriptor,
  isFilterAllowedForType,
  isPhase4Disabled,
  productTypeDisplayLabel,
} from '../type/filter-catalog'
import { CategoryByLevelSelect } from './CategoryByLevelSelect'

type DraftRule = 'skip' | FilterOverrideAction

interface Props {
  productType: ItemType
  override?: FilterOverrideView
  onClose: (changed?: boolean) => void
}

/**
 * Maps the general filter keys that admins can enable in base config to the
 * scope they unlock in the override drawer. Specific filter keys (material,
 * gender, etc.) don't appear here — those are *targets* of an override, not
 * scopes you can target.
 */
const FILTER_KEY_TO_SCOPE: Record<string, FilterOverrideScope> = {
  category: FilterOverrideScope.Category,
  subcategory: FilterOverrideScope.Subcategory,
  subcategory2: FilterOverrideScope.Subcategory2,
  vendor: FilterOverrideScope.Vendor,
}

const SCOPE_LABEL_KEY: Record<FilterOverrideScope, 'scope_category' | 'scope_subcategory' | 'scope_subcategory2' | 'scope_vendor'> = {
  [FilterOverrideScope.Category]:     'scope_category',
  [FilterOverrideScope.Subcategory]:  'scope_subcategory',
  [FilterOverrideScope.Subcategory2]: 'scope_subcategory2',
  [FilterOverrideScope.Vendor]:       'scope_vendor',
}

export const FilterOverrideDrawer: FC<Props> = ({ productType, override, onClose }) => {
  const isEdit = !!override
  const [messageApi, contextHolder] = message.useMessage()
  const [scopeType, setScopeType] = useState<FilterOverrideScope>(
    override?.scopeType ?? FilterOverrideScope.Subcategory,
  )
  const [scopeId, setScopeId] = useState<number | undefined>(override?.scopeId)
  const [scopeTitle, setScopeTitle] = useState<string | undefined>(
    override?.target?.title ?? undefined,
  )
  // Cascade parent ids for the target picker. When the user selects
  // Subcategory as the scope, the Category dropdown above narrows the
  // Subcategory dropdown to that category's children. Same idea for
  // Subcategory2 (which narrows by a picked Subcategory).
  const [parentCategoryId, setParentCategoryId] = useState<number | undefined>()
  const [parentSubcategoryId, setParentSubcategoryId] = useState<number | undefined>()
  const [draft, setDraft] = useState<Record<string, DraftRule>>(() => {
    const initial: Record<string, DraftRule> = {}
    for (const rule of override?.rules ?? []) {
      initial[rule.filterKey] = rule.action
    }
    return initial
  })

  useEffect(() => {
    setScopeType(override?.scopeType ?? FilterOverrideScope.Subcategory)
    setScopeId(override?.scopeId)
    setScopeTitle(override?.target?.title ?? undefined)
    setParentCategoryId(undefined)
    setParentSubcategoryId(undefined)
    setDraft(() => {
      const initial: Record<string, DraftRule> = {}
      for (const rule of override?.rules ?? []) {
        initial[rule.filterKey] = rule.action
      }
      return initial
    })
  }, [override])

  const { data: baseData, loading: baseLoading } = useQuery(GetFilterBaseConfig, {
    variables: { productType },
    fetchPolicy: 'cache-first',
  })

  // Scope options follow the filter catalog, not the base config: an override
  // can target any structural axis the product type supports (Category,
  // Sub-category 1, Sub-category 2, Vendor), independent of which of those
  // filters happen to be enabled in the base. Catalog order keeps them in a
  // natural Category → Sub-category 1 → Sub-category 2 → Vendor sequence.
  const scopeOptions = useMemo(() => {
    return Object.values(FILTER_CATALOG)
      .filter((d) => d.key in FILTER_KEY_TO_SCOPE && isFilterAllowedForType(d.key, productType))
      .map((d) => {
        const scope = FILTER_KEY_TO_SCOPE[d.key]
        return { value: scope, label: translate(SCOPE_LABEL_KEY[scope]) }
      })
  }, [productType])

  // If the currently-picked scope vanishes (base config edited elsewhere or
  // we're entering a product type that doesn't carry that scope), fall back
  // to the first valid option.
  useEffect(() => {
    if (isEdit) return
    if (scopeOptions.length === 0) return
    if (!scopeOptions.some((opt) => opt.value === scopeType)) {
      const first = scopeOptions[0].value
      setScopeType(first)
      setScopeId(undefined)
      setScopeTitle(undefined)
      setParentCategoryId(undefined)
      setParentSubcategoryId(undefined)
    }
  }, [scopeOptions, scopeType, isEdit])

  // Only show filters that are allowed for the active product type. The
  // drawer used to render the full 12-row catalog and grey-out the
  // unrelated ones; that was noisy (e.g. Material/Stone/Style showing up
  // on a Watch override) and obscured the relevant rules.
  const allRows = useMemo(() => {
    const baseEnabled = new Map(
      (baseData?.filterBaseConfig ?? [])
        .filter((row) => row.enabled)
        .map((row) => [row.filterKey, row.displayOrder]),
    )
    return Object.values(FILTER_CATALOG)
      .filter((descriptor) => isFilterAllowedForType(descriptor.key, productType))
      .map((descriptor) => ({
        ...descriptor,
        inBase: baseEnabled.has(descriptor.key),
        baseOrder: baseEnabled.get(descriptor.key) ?? 0,
      }))
  }, [baseData, productType])

  const [doCreate, { loading: creating }] = useMutation(CreateFilterOverride, {
    onCompleted: () => {
      messageApi.success(translate('override_save_success'))
      onClose(true)
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [doUpdate, { loading: updating }] = useMutation(UpdateFilterOverride, {
    onCompleted: () => {
      messageApi.success(translate('override_save_success'))
      onClose(true)
    },
    onError: (err) => messageApi.error(err.message),
  })

  const onScopeTypeChange = (next: FilterOverrideScope) => {
    setScopeType(next)
    setScopeId(undefined)
    setScopeTitle(undefined)
    setParentCategoryId(undefined)
    setParentSubcategoryId(undefined)
  }

  const onRuleChange = (key: string, value: DraftRule) => {
    setDraft((prev) => {
      const next = { ...prev }
      if (value === 'skip') delete next[key]
      else next[key] = value
      return next
    })
  }

  const rules: FilterOverrideRuleInput[] = useMemo(() => {
    return Object.entries(draft).map(([filterKey, action], index) => ({
      filterKey,
      action: action as FilterOverrideAction,
      displayOrder: index,
    }))
  }, [draft])

  const effectivePreview = useMemo(() => {
    const baseKeys = new Set(
      (baseData?.filterBaseConfig ?? [])
        .filter((row) => row.enabled)
        .map((row) => row.filterKey),
    )
    for (const rule of rules) {
      if (rule.action === FilterOverrideAction.Add) baseKeys.add(rule.filterKey)
      else if (rule.action === FilterOverrideAction.Remove) baseKeys.delete(rule.filterKey)
    }
    // Mirror the website's PHASE_4_DISABLED gate so the preview reflects what
    // the public site will actually show.
    return Array.from(baseKeys).filter((key) => !isPhase4Disabled(key))
  }, [rules, baseData])

  const onSubmit = () => {
    if (!isEdit && !scopeId) {
      messageApi.warning(translate('no_target_selected'))
      return
    }
    if (rules.length === 0) {
      messageApi.warning(translate('no_rules_configured'))
      return
    }

    if (isEdit && override) {
      const input: UpdateFilterOverrideInput = {
        id: override.id,
        rules,
      }
      doUpdate({ variables: { input } })
    } else {
      const input: CreateFilterOverrideInput = {
        productType,
        scopeType,
        scopeId: scopeId!,
        rules,
      }
      doCreate({ variables: { input } })
    }
  }

  const saving = creating || updating

  return (
    <Drawer
      title={isEdit ? translate('edit_override') : translate('add_override')}
      placement="right"
      width={640}
      onClose={() => onClose(false)}
      open
      destroyOnClose
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%', display: 'flex' }}>
          <Button onClick={() => onClose(false)} disabled={saving}>
            {translate('cancel')}
          </Button>
          <Button type="primary" loading={saving} onClick={onSubmit}>
            {translate('save')}
          </Button>
        </Space>
      }
    >
      {contextHolder}
      {/* Wait for base config before rendering the rule rows: their Add/Remove
          enabled-state depends on which filters are active in base (`inBase`).
          `allRows` is catalog-derived and never empty, so gate on `baseData`
          instead. On a cache hit this is instant; on error baseLoading clears
          and the form still renders (degrades, never hangs). */}
      <Spin spinning={baseLoading && !baseData}>
        <Form layout="vertical">
          <Form.Item style={{ marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Typography.Text
                type="secondary"
                style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.4 }}
              >
                {translate('product_type')}
              </Typography.Text>
              <Tag
                color="blue"
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  padding: '4px 18px',
                  margin: 0,
                  fontWeight: 600,
                }}
              >
                {productTypeDisplayLabel(productType)}
              </Tag>
            </div>
          </Form.Item>
          <Form.Item label={translate('scope')}>
            {scopeOptions.length === 0 && !isEdit ? (
              <Alert
                type="warning"
                showIcon
                message={translate('no_scopes_available')}
              />
            ) : (
              <Radio.Group
                value={scopeType}
                onChange={(e) => onScopeTypeChange(e.target.value)}
                disabled={isEdit}
                options={scopeOptions}
                optionType="button"
              />
            )}
          </Form.Item>
          <Form.Item label={translate('target')}>
            {isEdit ? (
              <Tag>{scopeTitle ?? `#${scopeId}`}</Tag>
            ) : scopeType === FilterOverrideScope.Vendor ? (
              <OverrideVendorSelect
                productType={productType}
                value={scopeId}
                placeholder={translate('scope_vendor')}
                onChange={(v, label) => {
                  setScopeId(v)
                  setScopeTitle(label)
                }}
              />
            ) : scopeType === FilterOverrideScope.Category ? (
              <CategoryByLevelSelect
                level={ItemCategoryLevel.Category}
                productType={productType}
                value={scopeId}
                placeholder={translate('scope_category')}
                onChange={(v, title) => {
                  setScopeId(v)
                  setScopeTitle(title)
                }}
              />
            ) : scopeType === FilterOverrideScope.Subcategory ? (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <CategoryByLevelSelect
                  level={ItemCategoryLevel.Category}
                  productType={productType}
                  value={parentCategoryId}
                  placeholder={translate('scope_category')}
                  onChange={(v) => {
                    setParentCategoryId(v)
                    setScopeId(undefined)
                    setScopeTitle(undefined)
                  }}
                />
                <CategoryByLevelSelect
                  level={ItemCategoryLevel.Subcategory}
                  productType={productType}
                  parentId={parentCategoryId}
                  disabled={!parentCategoryId}
                  value={scopeId}
                  placeholder={translate('scope_subcategory')}
                  onChange={(v, title) => {
                    setScopeId(v)
                    setScopeTitle(title)
                  }}
                />
              </Space>
            ) : scopeType === FilterOverrideScope.Subcategory2 ? (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <CategoryByLevelSelect
                  level={ItemCategoryLevel.Category}
                  productType={productType}
                  value={parentCategoryId}
                  placeholder={translate('scope_category')}
                  onChange={(v) => {
                    setParentCategoryId(v)
                    setParentSubcategoryId(undefined)
                    setScopeId(undefined)
                    setScopeTitle(undefined)
                  }}
                />
                <CategoryByLevelSelect
                  level={ItemCategoryLevel.Subcategory}
                  productType={productType}
                  parentId={parentCategoryId}
                  disabled={!parentCategoryId}
                  value={parentSubcategoryId}
                  placeholder={translate('scope_subcategory')}
                  onChange={(v) => {
                    setParentSubcategoryId(v)
                    setScopeId(undefined)
                    setScopeTitle(undefined)
                  }}
                />
                <CategoryByLevelSelect
                  level={ItemCategoryLevel.Subcategory2}
                  productType={productType}
                  parentId={parentSubcategoryId}
                  disabled={!parentSubcategoryId}
                  value={scopeId}
                  placeholder={translate('scope_subcategory2')}
                  onChange={(v, title) => {
                    setScopeId(v)
                    setScopeTitle(title)
                  }}
                />
              </Space>
            ) : null}
          </Form.Item>

          <Card size="small" title={translate('base_filters')} style={{ marginBottom: 16 }}>
            <Space wrap>
              {(baseData?.filterBaseConfig ?? [])
                .filter((r) => r.enabled)
                .map((r) => (
                  <Tag key={r.filterKey} color="default">
                    {r.label}
                  </Tag>
                ))}
              {(baseData?.filterBaseConfig ?? []).filter((r) => r.enabled).length === 0 && (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Space>
          </Card>

          <Card
            size="small"
            title={translate('override_rules')}
            bodyStyle={{ padding: 0 }}
            style={{ marginBottom: 16 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.4 }}>
                {translate('rules_filter_column').toUpperCase()}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.4 }}>
                {translate('rules_action_column').toUpperCase()}
              </Typography.Text>
            </div>
            {allRows.map((row, idx) => {
              const isAvailable = isFilterAllowedForType(row.key, productType)
              const phase4Disabled = isPhase4Disabled(row.key)
              const currentValue: DraftRule = draft[row.key] ?? 'skip'
              const segmentedDisabled = !isAvailable && currentValue === 'skip'
              // Only allow meaningful state changes. "Add" is pointless when the
              // filter is already active in base; "Remove" is pointless when it
              // isn't in base. Exactly one action stays selectable per filter.
              const addDisabled = !isAvailable || phase4Disabled || row.inBase
              const removeDisabled = !row.inBase
              const segClass =
                currentValue === FilterOverrideAction.Add
                  ? 'filter-rule-seg filter-rule-seg--add'
                  : currentValue === FilterOverrideAction.Remove
                    ? 'filter-rule-seg filter-rule-seg--remove'
                    : 'filter-rule-seg filter-rule-seg--skip'
              const segmented = (
                <Segmented
                  className={segClass}
                  value={currentValue}
                  disabled={segmentedDisabled}
                  onChange={(v) => onRuleChange(row.key, v as DraftRule)}
                  options={[
                    { label: translate('rule_skip'), value: 'skip' },
                    {
                      label: (
                        <span>
                          <PlusOutlined style={{ marginInlineEnd: 4 }} />
                          {translate('rule_add')}
                        </span>
                      ),
                      value: FilterOverrideAction.Add,
                      disabled: addDisabled,
                    },
                    {
                      label: (
                        <span>
                          <MinusOutlined style={{ marginInlineEnd: 4 }} />
                          {translate('rule_remove')}
                        </span>
                      ),
                      value: FilterOverrideAction.Remove,
                      disabled: removeDisabled,
                    },
                  ]}
                />
              )
              return (
                <div
                  key={row.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '12px 16px',
                    minHeight: 64,
                    borderBottom: idx === allRows.length - 1 ? 'none' : '1px solid #f0f0f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      minWidth: 0,
                      flex: '1 1 auto',
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.label}
                    </Typography.Text>
                    <Space size={4} wrap>
                      <Tag
                        color={row.kind === FilterKind.Specific ? 'purple' : 'blue'}
                        style={{ marginInlineEnd: 0 }}
                      >
                        {row.kind === FilterKind.Specific
                          ? translate('specific_filter')
                          : translate('general_filter')}
                      </Tag>
                      {phase4Disabled && (
                        <Tooltip title={translate('filter_phase4_disabled')}>
                          <Tag color="gold" style={{ marginInlineEnd: 0, cursor: 'help' }}>
                            {translate('filter_phase4_short')}
                          </Tag>
                        </Tooltip>
                      )}
                    </Space>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    {phase4Disabled ? (
                      <Tooltip title={translate('filter_phase4_disabled')}>
                        <span>{segmented}</span>
                      </Tooltip>
                    ) : (
                      segmented
                    )}
                  </div>
                </div>
              )
            })}
          </Card>

          <Card size="small" title={translate('effective_preview')}>
            {effectivePreview.length === 0 ? (
              <Alert
                type="warning"
                showIcon
                message={translate('no_rules_configured')}
              />
            ) : (
              <Space wrap>
                {effectivePreview.map((key) => {
                  const descriptor = getFilterDescriptor(key)
                  return (
                    <Tag key={key} color="geekblue">
                      {descriptor?.label ?? key}
                    </Tag>
                  )
                })}
              </Space>
            )}
          </Card>
        </Form>
      </Spin>
    </Drawer>
  )
}
